import { debounce } from "lodash";

import React, { Component, Fragment } from "react";
import { PropTypes } from "prop-types";
import { connect } from "react-redux";
import { Formik, Form } from "formik";

import ProjectWizardStepsNav from "./ProjectWizardStepsNav.jsx";
import WizardPage from "./WizardPage.jsx";
import ProjectWizardActionsPanel from "./ProjectWizardActionsPanel.jsx";
import FormikEffect from "../forms/formik/FormikEffect.jsx";
import ReadOnlyDependencyMessage from "./ReadOnlyDependencyMessage.jsx";

import { ROUTES } from "../../constants/routing.js";

import { selectTestingEnvironment } from "../../state/reducers/config/configSelectors.js";
import { selectSubmitAvailability } from "../../state/reducers/projectWizard/projectWizardSelectors.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class WizardPages extends Component {
  static propTypes = {
    config: PropTypes.object.isRequired,
    violationErrors: PropTypes.object,
    limits: PropTypes.object,
    provider: PropTypes.object,
    region: PropTypes.object,
    onChange: PropTypes.func,
    onReset: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    stepCompleteStates: PropTypes.array.isRequired,

    testingEnvironment: PropTypes.string,
    submitAvailable: PropTypes.bool.isRequired,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    submitAvailable: false
  };

  static PANEL_CHECK_DEBOUNCE = 400;

  constructor(props) {
    super(props);

    this.state = {
      values: props.initialValues,
      panelCompleteStates: {}
    };
  }

  componentDidMount() {
    // check form panel completeState on mount
    this.handleChange({}, { values: this.state.values }, true);
  }

  componentWillUnmount() {
    this.debouncedPanelValidityCheck.cancel();
  }

  resetScroll() {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  // -- HANDLERS -- //

  onNext = values => {
    const {
      config: { id: configId, steps },
      history
    } = this.props;
    const stepNumber = this.stepNumber();

    const redirect = `${ROUTES.PROJECT_WIZARD}/${configId}/${
      steps[stepNumber + 1].id
    }`;

    this.resetScroll();

    this.setState(
      {
        values
      },
      history.push(redirect)
    );
  };

  onPrev = () => {
    const {
      config: { id: configId, steps },
      history
    } = this.props;
    const stepNumber = this.stepNumber();

    const redirect = `${ROUTES.PROJECT_WIZARD}/${configId}/${
      steps[stepNumber - 1].id
    }`;

    this.resetScroll();

    history.push(redirect);
  };

  handleChange = (prevFormikProps, formikProps, force) => {
    const { onChange } = this.props;

    if (prevFormikProps.values === formikProps.values && !force) return null;

    this.debouncedPanelValidityCheck(formikProps.values);

    if (onChange) onChange(prevFormikProps, formikProps);
  };

  handleReset = () => {
    const {
      config: { id: configId, steps },
      match: {
        params: { step, id: projectId }
      },
      history,
      onReset
    } = this.props;

    this.resetScroll();
    onReset();

    if (step !== steps[0].id) {
      const redirect = `${ROUTES.PROJECT_WIZARD}/${configId}/${steps[0].id}`;
      history.push(`${redirect}${projectId ? projectId : ""}`);
    }
  };

  handleSubmit = (values, formikBag) => {
    // console.log("handleSubmit:", values, formikBag);
    if (this.isLastStep()) {
      return this.props.onSubmit(values, formikBag);
    } else {
      formikBag.setTouched({});
      formikBag.setSubmitting(false);
      this.onNext(values);
    }
  };

  handleValidate = values => {
    const { validate } = this.props;

    if (!validate) return {};

    const result = validate(values, this.stepNumber());

    return result;
  };

  checkPanelDependencies = values => {
    const {
      config: { steps },
      copy
    } = this.props;

    const step = steps[this.stepNumber()];

    if (!step.panelDependencyIndex) return null;

    const messages = copy("forms.validation_requirements");

    // -- Panel complete logic
    step.panelDependencyIndex.forEach(element => {
      step
        .validationSchema(messages)
        .validateAt(element, values)
        .then(result => {
          this.setState(state => {
            return {
              panelCompleteStates: {
                ...state.panelCompleteStates,
                [element]: true
              }
            };
          });
        })
        .catch(error => {
          this.setState(state => {
            return {
              panelCompleteStates: {
                ...state.panelCompleteStates,
                [element]: false
              }
            };
          });
        });
    });
  };

  debouncedPanelValidityCheck = debounce(
    this.checkPanelDependencies,
    WizardPages.PANEL_CHECK_DEBOUNCE
  );

  // -- HELPRES -- //

  isLastStep = () => {
    const {
      config: { steps },
      match: {
        params: { step }
      }
    } = this.props;
    return steps[steps.length - 1].id === step;
  };

  stepNumber = () => {
    const {
      config: { steps },
      match: {
        params: { step }
      }
    } = this.props;
    return steps.findIndex(element => element.id === step);
  };

  // -- RENDER -- //

  renderStepGroup = (stepNumber, formikProps) => {
    const { panelCompleteStates } = this.state;
    const {
      submitAvailable,
      config,
      violationErrors,
      limits,
      provider,
      region,
      match,
      zone
    } = this.props;

    return (
      <WizardPage
        key={`step-${stepNumber}`}
        stepNumber={stepNumber}
        violationErrors={violationErrors}
        limits={limits}
        estimateMode={!submitAvailable}
        isLastStep={this.isLastStep()}
        formikProps={formikProps}
        provider={provider}
        region={region}
        zone={zone}
        match={match}
        config={config}
        panelCompleteStates={panelCompleteStates}
      />
    );
  };

  render() {
    const { values } = this.state;
    const {
      config: { id: configId, steps, modalCopyKey },
      submitAvailable,
      provider,
      region,
      stepCompleteStates,
      onCancel,
      copy
    } = this.props;

    const stepNumber = this.stepNumber();
    const isLastStep = this.isLastStep();

    const copyKey = modalCopyKey || "project_wizard.modals";

    // Show message if an already set provider is invalid
    if (provider && provider.readonly) {
      return (
        <ReadOnlyDependencyMessage
          messageCopyKey={`project_wizard.provider_panel.messages.already_set_readonly`}
          actionCopyKey={"project_wizard.actions_panel"}
          onCancel={onCancel}
        />
      );
    }

    // Show message if an already set region is invalid
    if (region && region.readonly) {
      return (
        <ReadOnlyDependencyMessage
          messageCopyKey={`project_wizard.region_panel.messages.already_set_readonly`}
          actionCopyKey={"project_wizard.actions_panel"}
          onCancel={onCancel}
        />
      );
    }

    // build form
    return (
      <Fragment>
        <Formik
          initialValues={values}
          enableReinitialize={false}
          validate={this.handleValidate}
          onSubmit={this.handleSubmit}
        >
          {formikProps => {
            return (
              <Form className="ui form" noValidate>
                <FormikEffect onChange={this.handleChange} />

                {steps.length > 1 && (
                  <ProjectWizardStepsNav
                    configId={configId}
                    stepPosition={stepNumber}
                    steps={steps}
                    stepCompleteStates={stepCompleteStates}
                  />
                )}

                {this.renderStepGroup(stepNumber, formikProps)}

                <ProjectWizardActionsPanel
                  // project wizard is now shown to all users, but actual creation ability is managed by the flag
                  next={!isLastStep}
                  prev={stepNumber > 0}
                  onPrev={this.onPrev}
                  submit={isLastStep && submitAvailable}
                  useSubmitModal={submitAvailable}
                  reset={true}
                  onCancel={onCancel}
                  onReset={this.handleReset}
                  panelCopy={copy("project_wizard.actions_panel")}
                  modalCopy={copy(copyKey)}
                />

                {/* {testingEnvironment === ENVIRONMENTS.DEVELOPMENT && (
                  <Debug values={formikProps.values} />
                )} */}
              </Form>
            );
          }}
        </Formik>
      </Fragment>
    );
  }
}

const mapStateToProps = (state, props) => ({
  testingEnvironment: selectTestingEnvironment(state),
  submitAvailable: selectSubmitAvailability(state, props),
  copy: selectCopy(state)
});

export default connect(mapStateToProps)(WizardPages);
